import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useUserStore } from '../store/userStore';
import { userService, User, UserRole } from '../services/userService';
import { companyService, Company } from '../services/companyService';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import { useProgress } from '../components/TopProgressBar';
import Spinner from '../components/Spinner';
import Button from '../components/Button';

const UsersManagementPage: React.FC = () => {
  const { isAdmin } = useUserStore();
  const toast = useToast();
  const { start: progressStart, done: progressDone, showLoader, hideLoader } = useProgress();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showAssignCompanyModal, setShowAssignCompanyModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    userId: '',
    email: '',
    name: '',
    role: 'asesor' as UserRole,
    password: '',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: 'asesor' as UserRole,
    password: '',
  });

  const [showEditModal, setShowEditModal] = useState(false);
  
  // Estados para diálogos
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
      loadCompanies();
    }
  }, [isAdmin]);

  // Loader inmediato al montar
  useLayoutEffect(() => {
    showLoader('Cargando usuarios...');
    progressStart();
    return () => { hideLoader(); progressDone(); };
  }, []); // eslint-disable-line

  const loadUsers = async (): Promise<User[]> => {
    try {
      setLoading(true);
      progressStart();
      const data = await userService.listUsers();
      setUsers(data);
      return data;
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error al cargar usuarios');
      return [];
    } finally {
      setLoading(false);
      progressDone();
      hideLoader();
    }
  };

  // Recarga usuarios y sincroniza selectedUser si hay uno seleccionado
  const reloadAndSyncSelected = async (userId?: string) => {
    const data = await loadUsers();
    if (userId) {
      const fresh = data.find(u => u.userId === userId);
      if (fresh) setSelectedUser(fresh);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await companyService.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.password) {
      toast.error('La contraseña es requerida para crear un usuario');
      return;
    }
    try {
      showLoader('Creando usuario...');
      const normalizedEmail = formData.email.toLowerCase().trim();
      const normalizedUserId = formData.userId.toLowerCase().trim();
      await userService.createOrUpdateUser(normalizedUserId, normalizedEmail, formData.name, formData.role, formData.password);
      setShowCreateModal(false);
      setFormData({ userId: '', email: '', name: '', role: 'asesor', password: '' });
      loadUsers();
      toast.success('Usuario creado');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Error al crear usuario');
    } finally {
      hideLoader();
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      showLoader('Actualizando usuario...');
      const updates: any = {
        name: editFormData.name,
        email: editFormData.email.toLowerCase().trim(),
        role: editFormData.role,
      };
      if (editFormData.password) updates.password = editFormData.password;
      await userService.updateUser(selectedUser.userId, updates);
      setShowEditModal(false);
      setSelectedUser(null);
      setEditFormData({ name: '', email: '', role: 'asesor', password: '' });
      loadUsers();
      toast.success('Usuario actualizado');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al actualizar usuario');
    } finally {
      hideLoader();
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      showLoader(user.active ? 'Desactivando usuario...' : 'Activando usuario...');
      await userService.updateUser(user.userId, { active: !user.active });
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al cambiar estado del usuario');
    } finally {
      hideLoader();
    }
  };

  const handleAssignCompany = async (configId: string) => {
    if (!selectedUser) return;
    try {
      showLoader('Asignando empresa...');
      await userService.assignCompany(selectedUser.userId, configId);
      await reloadAndSyncSelected(selectedUser.userId);
      toast.success('Empresa asignada');
    } catch (error) {
      console.error('Error assigning company:', error);
      toast.error('Error al asignar empresa');
    } finally {
      hideLoader();
    }
  };

  const handleUnassignCompany = async (userId: string, configId: string) => {
    const company = companies.find(c => c.configId === configId);
    const user = users.find(u => u.userId === userId);
    setConfirmDialog({
      isOpen: true,
      title: 'Desasignar empresa',
      message: `¿Desasignar la empresa "${company?.nombreEmpresa}" de ${user?.name}?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          showLoader('Desasignando empresa...');
          await userService.unassignCompany(userId, configId);
          await reloadAndSyncSelected(userId);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          toast.success('Empresa desasignada');
        } catch (error) {
          console.error('Error unassigning company:', error);
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          toast.error('Error al desasignar empresa');
        } finally {
          hideLoader();
        }
      },
    });
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-bg">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-brand-surface shadow-sm flex items-center justify-center">
            <svg className="w-7 h-7 text-brand-mid/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-base font-bold text-brand-dark">Acceso Denegado</h2>
          <p className="text-brand-mid text-sm mt-1">Solo los administradores pueden acceder</p>
        </div>
      </div>
    );
  }

  // ── helpers de estilo ────────────────────────────────────────────────────────
  const roleBadge = (role: string) => {
    if (role === 'administrador') return 'bg-brand-dark/10 text-brand-dark';
    if (role === 'operador')      return 'bg-brand-mid/20 text-brand-mid';
    return 'bg-brand-yellow/20 text-brand-dark';
  };

  const inputCls = 'w-full px-4 py-2.5 bg-brand-light border-0 rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow';
  const labelCls = 'block text-xs font-semibold text-brand-dark mb-1.5';

  return (
    <div className="p-4 md:p-8 bg-brand-bg min-h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-4 md:mb-6 gap-3">
          <div>
            <h1 className="text-lg md:text-2xl font-extrabold text-brand-dark leading-tight">Gestión de Usuarios</h1>
            <p className="text-brand-mid text-xs md:text-sm mt-0.5">{users.length} usuario{users.length !== 1 ? 's' : ''} registrado{users.length !== 1 ? 's' : ''}</p>
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Crear Usuario</span>
            <span className="sm:hidden">Crear</span>
          </Button>
        </div>

        {/* Vista mobile — cards apiladas */}
        <div className="md:hidden space-y-3">
          {users.length === 0 && (
            <div className="py-12 text-center text-brand-mid text-sm">No hay usuarios registrados</div>
          )}
          {users.map((user) => (
            <div key={user.userId} className="bg-brand-surface rounded-2xl border border-brand-border p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="avatar-initial w-10 h-10 rounded-full bg-brand-dark flex items-center justify-center text-brand-yellow font-bold text-sm flex-shrink-0">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-brand-dark truncate">{user.name}</p>
                  <p className="text-[10px] text-brand-mid font-mono truncate">{user.userId}</p>
                  <p className="text-xs text-brand-mid truncate mt-0.5">{user.email}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${roleBadge(user.role)}`}>
                    {user.role}
                  </span>
                  <button onClick={() => handleToggleActive(user)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition ${
                      user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>
                    {user.active ? 'Activo' : 'Inactivo'}
                  </button>
                </div>
              </div>

              {user.assignedCompanies && user.assignedCompanies.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {user.assignedCompanies.map((configId) => {
                    const company = companies.find(c => c.configId === configId);
                    return (
                      <span key={configId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-brand-light text-brand-dark font-medium">
                        {company?.nombreEmpresa || configId}
                        <button onClick={() => handleUnassignCompany(user.userId, configId)} className="text-red-400 hover:text-red-600 font-bold leading-none">×</button>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-brand-border">
                <Button variant="secondary" size="sm" fullWidth
                  onClick={() => { setSelectedUser(user); setEditFormData({ name: user.name, email: user.email, role: user.role, password: '' }); setShowEditModal(true); }}>
                  Editar
                </Button>
                <Button variant="secondary" size="sm" fullWidth
                  onClick={() => { setSelectedUser(user); setShowAssignCompanyModal(true); }}>
                  Empresas
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Vista desktop — tabla */}
        <div className="hidden md:block bg-brand-surface rounded-2xl border border-brand-border overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {['Usuario', 'Email', 'Rol', 'Estado', 'Empresas Asignadas', 'Acciones'].map(h => (
                  <th key={h} className="px-6 py-3.5 text-left text-[10px] font-bold text-brand-mid uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((user) => (
                <tr key={user.userId} className="hover:bg-brand-light/50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="avatar-initial w-8 h-8 rounded-full bg-brand-dark flex items-center justify-center text-brand-yellow font-bold text-xs flex-shrink-0">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-brand-dark">{user.name}</p>
                        <p className="text-[10px] text-brand-mid font-mono">{user.userId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-brand-mid">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${roleBadge(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleToggleActive(user)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition hover:opacity-80 ${
                        user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>
                      {user.active ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.assignedCompanies?.map((configId) => {
                        const company = companies.find(c => c.configId === configId);
                        return (
                          <span key={configId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-brand-light text-brand-dark font-medium">
                            {company?.nombreEmpresa || configId}
                            <button onClick={() => handleUnassignCompany(user.userId, configId)} className="text-red-400 hover:text-red-600 font-bold leading-none">×</button>
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm"
                        onClick={() => { setSelectedUser(user); setEditFormData({ name: user.name, email: user.email, role: user.role, password: '' }); setShowEditModal(true); }}>
                        Editar
                      </Button>
                      <Button variant="secondary" size="sm"
                        onClick={() => { setSelectedUser(user); setShowAssignCompanyModal(true); }}>
                        Empresas
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="py-16 text-center text-brand-mid text-sm">No hay usuarios registrados</div>
          )}
        </div>

        {/* Modal crear usuario */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-brand-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="bg-brand-dark text-white px-6 py-5">
                <h2 className="text-lg font-bold">Crear Usuario</h2>
              </div>
              <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                <div>
                  <label className={labelCls}>User ID (Username de Cognito)</label>
                  <input type="text" value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    className={inputCls} placeholder="juan.perez" required />
                  <p className="text-[10px] text-brand-mid mt-1">Se creará como: agente_{formData.userId || 'username'}</p>
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={inputCls} required />
                </div>
                <div>
                  <label className={labelCls}>Nombre</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={inputCls} required />
                </div>
                <div>
                  <label className={labelCls}>Rol</label>
                  <select value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    className={inputCls}>
                    <option value="asesor">Asesor</option>
                    <option value="operador">Operador</option>
                    <option value="administrador">Administrador</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Contraseña</label>
                  <input type="password" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={inputCls} placeholder="Mínimo 8 caracteres" required />
                </div>
                <div className="flex gap-3 pt-1">
                  <Button type="submit" variant="primary" fullWidth loading={loading}>
                    {loading ? 'Creando...' : 'Crear'}
                  </Button>
                  <Button type="button" variant="secondary" fullWidth onClick={() => setShowCreateModal(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal asignar empresa */}
        {showAssignCompanyModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-brand-surface rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
              <div className="bg-brand-dark text-white px-6 py-5">
                <h2 className="text-lg font-bold">Asignar Empresa</h2>
                <p className="text-white/70 text-sm mt-0.5">{selectedUser.name} · {selectedUser.assignedCompanies?.length || 0} asignada(s)</p>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Asignadas */}
                {selectedUser.assignedCompanies && selectedUser.assignedCompanies.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-brand-dark mb-2">Asignadas</p>
                    <div className="space-y-2">
                      {companies.filter(c => selectedUser.assignedCompanies?.includes(c.configId)).map((company) => (
                        <div key={company.configId} className="flex items-center justify-between px-4 py-3 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30">
                          <div>
                            <p className="font-semibold text-brand-dark text-sm">{company.nombreEmpresa}</p>
                            <p className="text-[10px] text-brand-mid font-mono">{company.configId}</p>
                          </div>
                          <button onClick={() => handleUnassignCompany(selectedUser.userId, company.configId)}
                            className="text-xs text-red-500 hover:text-red-700 font-semibold ml-3">
                            Quitar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Disponibles */}
                <div>
                  <p className="text-xs font-bold text-brand-dark mb-2">Disponibles</p>
                  <div className="space-y-2">
                    {companies.filter(c => !selectedUser.assignedCompanies?.includes(c.configId)).map((company) => (
                      <button key={company.configId} onClick={() => handleAssignCompany(company.configId)}
                        className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 bg-brand-light hover:border-brand-yellow/40 hover:bg-brand-yellow/5 transition">
                        <p className="font-semibold text-brand-dark text-sm">{company.nombreEmpresa}</p>
                        <p className="text-[10px] text-brand-mid font-mono">{company.configId}</p>
                      </button>
                    ))}
                    {companies.filter(c => !selectedUser.assignedCompanies?.includes(c.configId)).length === 0 && (
                      <p className="text-center text-brand-mid text-sm py-4">Todas las empresas ya están asignadas</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-brand-light border-t border-brand-border rounded-b-2xl">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => { setShowAssignCompanyModal(false); setSelectedUser(null); }}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal editar usuario */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-brand-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="bg-brand-dark text-white px-6 py-5">
                <h2 className="text-lg font-bold">Editar Usuario</h2>
                <p className="text-white/70 text-sm mt-0.5">{selectedUser.name}</p>
              </div>
              <form onSubmit={handleEditUser} className="p-6 space-y-4">
                <div>
                  <label className={labelCls}>Nombre</label>
                  <input type="text" value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className={inputCls} required />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className={inputCls} required />
                </div>
                <div>
                  <label className={labelCls}>Rol</label>
                  <select value={editFormData.role}
                    onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value as UserRole })}
                    className={inputCls}>
                    <option value="asesor">Asesor</option>
                    <option value="operador">Operador</option>
                    <option value="administrador">Administrador</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Nueva Contraseña <span className="text-brand-mid font-normal">(opcional)</span></label>
                  <input type="password" value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    className={inputCls} placeholder="Dejar vacío para no cambiar" />
                </div>
                <div className="flex gap-3 pt-1">
                  <Button type="submit" variant="primary" fullWidth>
                    Guardar
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    fullWidth
                    onClick={() => { setShowEditModal(false); setSelectedUser(null); setEditFormData({ name: '', email: '', role: 'asesor', password: '' }); }}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          type={confirmDialog.type}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default UsersManagementPage;
